import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';
import { checkPraticaAccess } from '../utils/praticaAuth';
import { 
  ArrowRightIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import API_CONFIG from '../config/api';

// Import dei componenti modulari
import GestoreStatiPratica from '../components/pratica/GestoreStatiPratica';
import {
  NavigazioneTabs,
  TabRiepilogo,
  TabAnagrafica,
  TabComunicazioni,
  TabOfferte,
  TabPreventivi,
  TabDocumenti,
  TabContratti
} from '../components/pratica';

const PraticaDetailPageModulare = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useOutletContext();
  
  // Stati principali
  const [activeTab, setActiveTab] = useState('riepilogo');
  const [pratica, setPratica] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ruoloUtente, setRuoloUtente] = useState('agente');
  
  // Stati per dati di supporto
  const [agenzie, setAgenzie] = useState([]);
  const [agenti, setAgenti] = useState([]);
  const [contratti, setContratti] = useState([]);
  
  // Gerarchia per controlli di autorizzazione
  const [isAdmin, setIsAdmin] = useState(false);
  const { userNode, subordinateNodes, allNodes, loading: hierarchyLoading } = useNetworkHierarchy(user);

  // Stati per le modal
  const [showRichiestaIntegrazioneModal, setShowRichiestaIntegrazioneModal] = useState(false);
  const [showApprovaDocumentiModal, setShowApprovaDocumentiModal] = useState(false);
  const [showRiassegnazioneModal, setShowRiassegnazioneModal] = useState(false);
  const [showAssegnazioneAgenteModal, setShowAssegnazioneAgenteModal] = useState(false);
  
  const [formIntegrazioneData, setFormIntegrazioneData] = useState({
    note: '',
    motivazione: '',
    documentiDaIntegrare: []
  });
  const [erroreValidazione, setErroreValidazione] = useState(null);
  
  // Stati per riassegnazione e assegnazione
  const [agenzieDisponibili, setAgenzieDisponibili] = useState([]);
  const [agentiDisponibili, setAgentiDisponibili] = useState([]);
  const [nuovaAgenzia, setNuovaAgenzia] = useState('');
  const [nuovoAgente, setNuovoAgente] = useState('');
  const [loadingRiassegnazione, setLoadingRiassegnazione] = useState(false);
  const [loadingAssegnazioneAgente, setLoadingAssegnazioneAgente] = useState(false);

  // Ref per il componente GestoreStatiPratica
  const gestoreStatiRef = useRef(null);

  useEffect(() => {
    if (id) {
      initializeData();
    }
  }, [id]);

  // Effetto per determinare il ruolo dell'utente in base alla pratica
  useEffect(() => {
    if (pratica && user) {
      determineUserRole();
    }
  }, [pratica, user]);

  // Determina il ruolo dell'utente in base alla sua relazione con la pratica
  const determineUserRole = () => {
    if (!pratica || !user) return;

    const userRole = user.role || user.ruolo || '';
    const isAdmin = userRole === 'admin';
    const isAgenteAssegnato = pratica.idAgente && pratica.idAgente === user.id;
    const isAgenziaPrimaria = pratica.idAgenziaPrimaria && userNode && userNode.id === pratica.idAgenziaPrimaria;

    console.log('🔍 Determinazione ruolo utente:', {
      userRole,
      isAdmin,
      isAgenteAssegnato,
      isAgenziaPrimaria,
      praticaIdAgente: pratica.idAgente,
      userId: user.id,
      userNodeId: userNode?.id,
      praticaIdAgenziaPrimaria: pratica.idAgenziaPrimaria
    });

    // Determina il ruolo effettivo per questa pratica
    let ruoloPerPratica = 'agente'; // default

    if (isAdmin) {
      ruoloPerPratica = 'admin';
    } else if (isAgenteAssegnato) {
      ruoloPerPratica = 'agente';
    } else if (isAgenziaPrimaria && userRole === 'agenzia') {
      ruoloPerPratica = 'agenzia';
    } else {
      // Per altri casi (team leader, etc.) usa il ruolo base
      ruoloPerPratica = userRole;
    }

    console.log(`✅ Ruolo determinato per pratica: ${ruoloPerPratica}`);
    setRuoloUtente(ruoloPerPratica);
    setIsAdmin(isAdmin);
  };
  
  // Controllo di autorizzazione quando cambiano i dati della gerarchia
  useEffect(() => {
    if (pratica && user && !hierarchyLoading) {
      // Bypass diretto per admin
      if (isAdmin || user.role === 'admin' || user.ruolo === 'admin') {
        console.log('✅ [FRONTEND BYPASS] Admin rilevato, skip controlli autorizzazione');
        return;
      }
      
      const hasAccess = checkPraticaAccess(pratica, user, subordinateNodes, isAdmin, navigate, toast, userNode);
      if (!hasAccess) {
        return; // La funzione checkPraticaAccess ha già gestito il redirect
      }
    }
  }, [pratica, user, subordinateNodes, hierarchyLoading, isAdmin, userNode]);

  const initializeData = async () => {
    try {
      setLoading(true);
      
      // Determina se l'utente può accedere agli agenti
      const userRole = user?.role || user?.ruolo || '';
      const canAccessAgenti = userRole === 'admin' || userRole === 'agenzia';
      
      // Carica sempre la pratica e le agenzie
      const promises = [
        fetchPraticaDetail(),
        fetchAgenzie()
      ];
      
      // Carica gli agenti solo se l'utente ha i permessi
      if (canAccessAgenti) {
        promises.push(fetchAgenti());
      } else {
        // Imposta array vuoto per gli agenti se l'utente non ha accesso
        setAgenti([]);
      }
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Errore durante l\'inizializzazione:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPraticaDetail = async () => {
    try {
      const response = await api.get(`/pratica/${id}`);
      
      // Il backend restituisce direttamente la pratica, non un wrapper success/data
      if (response.data && response.data.id) {
        const praticaData = response.data;
        setPratica(praticaData);
        
        // Carica i contratti quando viene caricata la pratica
        fetchContratti();
        
        // Controllo di autorizzazione dopo aver caricato la pratica
        if (user && !hierarchyLoading) {
          const hasAccess = checkPraticaAccess(praticaData, user, subordinateNodes, isAdmin, navigate, toast, userNode);
          if (!hasAccess) {
            return; // La funzione checkPraticaAccess ha già gestito il redirect
          }
        }
      } else {
        toast.error('Pratica non trovata');
        navigate('/nlt/pratiche');
      }
    } catch (error) {
      console.error('Errore durante il caricamento della pratica:', error);
      
      // Se è un errore 404, la pratica non esiste
      if (error.response?.status === 404) {
        toast.error('Pratica non trovata');
      } else {
        toast.error('Errore durante il caricamento della pratica');
      }
      navigate('/nlt/pratiche');
    }
  };

  const fetchContratti = async () => {
    try {
      const response = await api.get(`/pratica/${id}/contratti`);
      console.log('📋 fetchContratti response:', response.data);
      if (response.data.success) {
        const contrattiFetched = response.data.contratti || [];
        console.log('📋 Contratti caricati:', contrattiFetched);
        setContratti(contrattiFetched);
      } else {
        console.log('⚠️ Nessun contratto trovato');
        setContratti([]);
      }
    } catch (error) {
      console.error('❌ Errore nel caricamento contratti:', error);
      setContratti([]);
    }
  };

  const fetchAgenzie = async () => {
    try {
      const response = await api.get(`/pratica/agenzie`);
      // Il backend restituisce direttamente {agenzie: [...]} senza wrapper success
      if (response.data && response.data.agenzie) {
        setAgenzie(response.data.agenzie);
      } else {
        setAgenzie([]);
      }
    } catch (error) {
      console.error('Errore durante il caricamento delle agenzie:', error);
      setAgenzie([]);
    }
  };

  const fetchAgenti = async () => {
    try {
      const response = await api.get(`/network/utenti/agenti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        setAgenti(response.data.agenti || []);
      }
    } catch (error) {
      console.error('Errore durante il caricamento degli agenti:', error);
      // Non usar mock - se non esiste l'endpoint, array vuoto
      setAgenti([]);
    }
  };

  // Funzioni di utilità
  const formatDate = (dateString) => {
    if (!dateString) return 'Non disponibile';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    } catch (error) {
      return 'Data non valida';
    }
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    
    try {
      const now = new Date();
      const messageDate = new Date(date);
      const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'ora';
      if (diffInMinutes < 60) return `${diffInMinutes} min fa`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h fa`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}g fa`;
    } catch (error) {
      return '';
    }
  };

  const getAgenziaName = (idAgenzia) => {
    // Prima cerca nell'array agenzie (caricato dall'API)
    const agenzia = agenzie.find(a => a.id === idAgenzia);
    if (agenzia) return agenzia.nome;
    
    // Altrimenti cerca in allNodes (dalla gerarchia rete)
    const nodo = allNodes.find(n => n.id === idAgenzia);
    if (!nodo) return `Nodo ${idAgenzia}`;
    
    // Se è un'agenzia primaria, mostra solo il nome
    if (nodo.tipo === 'agenzia_primaria') {
      return nodo.nome;
    }
    
    // Solo l'admin vede il percorso gerarchico completo
    // Gli altri ruoli vedono solo l'agenzia primaria
    if (!isAdmin) {
      // Risali fino all'agenzia primaria
      let current = nodo;
      while (current && current.tipo !== 'agenzia_primaria') {
        current = allNodes.find(n => n.id === current.parentId);
      }
      return current ? current.nome : nodo.nome;
    }
    
    // SOLO PER ADMIN: costruisci il percorso gerarchico completo
    if (nodo.parentId) {
      const parent = allNodes.find(n => n.id === nodo.parentId);
      if (parent && parent.tipo === 'agenzia_primaria') {
        return `${parent.nome} - ${nodo.nome.replace('Agente ', '')}`;
      } else if (parent) {
        // Ricorsivamente trova l'agenzia primaria
        let current = parent;
        const path = [nodo.nome.replace('Agente ', '').replace('Team Leader ', '')];
        
        while (current && current.tipo !== 'agenzia_primaria') {
          path.unshift(current.nome.replace('Agente ', '').replace('Team Leader ', ''));
          current = allNodes.find(n => n.id === current.parentId);
        }
        
        if (current && current.tipo === 'agenzia_primaria') {
          path.unshift(current.nome);
          return path.join(' - ');
        }
      }
    }
    
    return nodo.nome;
  };

  const getAgenteName = (idAgente) => {
    // Prima prova a usare i dati dell'agente già inclusi nella pratica
    if (pratica?.agente && pratica.agente.id === idAgente) {
      return `${pratica.agente.nome} ${pratica.agente.cognome}`;
    }
    
    // Fallback: cerca negli agenti disponibili caricati
    const agente = agenti.find(a => a.id === idAgente);
    return agente ? `${agente.nome} ${agente.cognome}` : 'Agente non trovato';
  };

  const isPraticaModificabileAgente = () => {
    return pratica && !isInAttesaRevisione();
  };

  const isInAttesaRevisione = () => {
    return (pratica?.statoPratica || pratica?.stato) === 'in_attesa_revisione_agente';
  };

  const isOfferteTabDisabled = () => {
    return (pratica?.statoPratica || pratica?.stato) === 'attesa_preventivo';
  };

  const isCampoModificabileAgente = (campo) => {
    if (ruoloUtente === 'admin') return true;
    if (!isPraticaModificabileAgente()) return false;
    
    // Lista dei campi sempre modificabili per l'agente
    const campiModificabiliAgente = [
      'email', 'telefono', 'note'
    ];
    
    return campiModificabiliAgente.includes(campo);
  };

  // Funzioni per gestire le autorizzazioni basate sul ruolo reale dell'utente
  const getRuoloRealeUtente = () => {
    return user?.role || user?.ruolo || 'agente';
  };

  const getOpzioniRuoloDisponibili = () => {
    const ruoloReale = getRuoloRealeUtente();
    const isAgenteAssegnato = pratica?.idAgente && pratica.idAgente === user?.id;
    const isAgenziaPrimaria = pratica?.idAgenziaPrimaria && userNode && userNode.id === pratica.idAgenziaPrimaria;
    
    switch (ruoloReale) {
      case 'admin':
        // Admin vede tutti i ruoli
        return [
          { value: 'agente', label: 'Agente' },
          { value: 'agenzia', label: 'Agenzia' },
          { value: 'admin', label: 'Admin' }
        ];
      case 'agenzia':
        // Agenzia vede agenzia e agente se è l'agenzia primaria o l'agente assegnato
        const opzioniAgenzia = [];
        if (isAgenziaPrimaria) {
          opzioniAgenzia.push({ value: 'agenzia', label: 'Agenzia' });
        }
        if (isAgenteAssegnato || isAgenziaPrimaria) {
          opzioniAgenzia.push({ value: 'agente', label: 'Agente' });
        }
        return opzioniAgenzia.length > 0 ? opzioniAgenzia : [{ value: 'agente', label: 'Agente' }];
      case 'team_leader':
        // Team leader vede solo agente
        return [{ value: 'agente', label: 'Agente' }];
      case 'subagente':
      case 'agente':
      case 'segnalatore':
      default:
        // Agenti vedono solo agente (se assegnati alla pratica o per default)
        return [{ value: 'agente', label: 'Agente' }];
    }
  };

  const canAssegnareAgenti = () => {
    const ruoloReale = getRuoloRealeUtente();
    // Solo admin e agenzie possono assegnare agenti
    return ruoloReale === 'admin' || ruoloReale === 'agenzia' || ruoloReale === 'team_leader';
  };

  // Funzioni per azioni specifiche
  const richiedeIntegrazione = async () => {
    setShowRichiestaIntegrazioneModal(true);
  };

  const approvaDocumenti = async () => {
    setErroreValidazione(null); // Reset errore precedente
    setShowApprovaDocumentiModal(true);
  };

  // Funzione per approvare la pratica (solo admin)
  const approvaPratica = async () => {
    try {
      const response = await api.put(`/pratica/${id}/approva-pratica`, {
        note: 'Pratica approvata dall\'amministratore'
      });
      
      toast.success('Pratica approvata con successo');
      fetchPraticaDetail(); // Ricarica la pratica per aggiornare lo stato
    } catch (error) {
      console.error('Errore nell\'approvazione pratica:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'approvazione della pratica');
    }
  };

  // Funzione per rifiutare la pratica (solo admin)
  const rifiutaPratica = async () => {
    const motivazione = prompt('Inserisci la motivazione del rifiuto:');
    if (!motivazione) {
      toast.warning('È necessario inserire una motivazione per il rifiuto');
      return;
    }

    try {
      const response = await api.put(`/pratica/${id}/rifiuta-pratica`, {
        motivazione: motivazione
      });
      
      toast.success('Pratica rifiutata');
      fetchPraticaDetail(); // Ricarica la pratica per aggiornare lo stato
    } catch (error) {
      console.error('Errore nel rifiuto pratica:', error);
      toast.error('Errore nel rifiuto della pratica');
    }
  };

  // Funzione per approvazione condizionata (solo admin)
  const condizionaPratica = async () => {
    const condizioni = prompt('Inserisci le condizioni per l\'approvazione condizionata:');
    if (!condizioni) {
      toast.warning('È necessario specificare le condizioni');
      return;
    }
    try {
      await api.put(`/pratica/${id}/condiziona`, { condizioni });
      toast.success('Pratica approvata con condizioni');
      fetchPraticaDetail();
    } catch (error) {
      console.error('Errore nell\'approvazione condizionata:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'approvazione condizionata');
    }
  };

  // Chiude con esito negativo una pratica condizionata (solo admin)
  const chiudiPraticaCondizionata = async () => {
    const motivazione = prompt('Motivazione dell\'esito negativo:');
    if (!motivazione) {
      toast.warning('È necessario inserire una motivazione');
      return;
    }
    try {
      await api.put(`/pratica/${id}/esito-negativo`, { motivazione });
      toast.success('Pratica chiusa con esito negativo');
      fetchPraticaDetail();
    } catch (error) {
      console.error('Errore nella chiusura con esito negativo:', error);
      toast.error(error.response?.data?.error || 'Errore nella chiusura della pratica');
    }
  };

  // Funzione per attivare il contratto (solo admin)
  const attivaContratto = async () => {
    try {
      const response = await api.put(`/pratica/${id}/attiva-contratto`, {
        note: 'Contratto verificato e attivato'
      });
      
      toast.success('Contratto attivato con successo');
      fetchPraticaDetail(); // Ricarica la pratica per aggiornare lo stato
      fetchContratti(); // Ricarica i contratti
    } catch (error) {
      console.error('Errore nell\'attivazione contratto:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'attivazione del contratto');
    }
  };

  const handleTornaPraticaAvviata = async () => {
    try {
      const response = await api.put(`/pratica/${id}/riporta-pratica-avviata`, {
        note: 'Pratica riportata a "Pratica Avviata" per permettere la creazione di nuovi preventivi'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data && response.data.message) {
        toast.success(response.data.message);
        fetchPraticaDetail(); // Ricarica i dati della pratica
      }
    } catch (error) {
      console.error('Errore nel ritorno a pratica avviata:', error);
      const errorMessage = error.response?.data?.error || 'Errore nel riportare la pratica a "Pratica Avviata"';
      toast.error(errorMessage);
    }
  };

  // Funzioni per gestire le modal
  const handleRichiestaIntegrazione = async () => {
    if (!formIntegrazioneData.note.trim() || !formIntegrazioneData.motivazione.trim()) {
      toast.error('Note e motivazione sono obbligatorie');
      return;
    }

    try {
      const response = await api.put(`/pratica/${id}/richiedi-integrazione`, {
        note: formIntegrazioneData.note,
        motivazione: formIntegrazioneData.motivazione,
        documentiDaIntegrare: formIntegrazioneData.documentiDaIntegrare
      });
      
      toast.success('Richiesta integrazione inviata con successo');
      setShowRichiestaIntegrazioneModal(false);
      setFormIntegrazioneData({ note: '', motivazione: '', documentiDaIntegrare: [] });
      fetchPraticaDetail(); // Ricarica la pratica per aggiornare lo stato
    } catch (error) {
      console.error('Errore nella richiesta integrazione:', error);
      toast.error(error.response?.data?.error || 'Errore nella richiesta di integrazione');
    }
  };

  const handleApprovaDocumenti = async (forzaApprovazione = false) => {
    try {
      const response = await api.put(`/pratica/${id}/approva-documenti`, {
        forzaApprovazione: forzaApprovazione,
        note: 'Documenti approvati dall\'amministratore'
      });
      
      toast.success('Documenti approvati con successo');
      setShowApprovaDocumentiModal(false);
      setErroreValidazione(null);
      fetchPraticaDetail(); // Ricarica la pratica per aggiornare lo stato
    } catch (error) {
      console.error('Errore nell\'approvazione documenti:', error);
      
      // Se l'errore è per documenti mancanti, mostra l'opzione di forzare
      if (error.response?.status === 400 && error.response?.data?.documentiMancanti) {
        setErroreValidazione({
          documentiMancanti: error.response.data.documentiMancanti,
          messaggio: error.response.data.error
        });
        return; // La modal rimane aperta per permettere di forzare l'approvazione
      }
      
      toast.error(error.response?.data?.error || 'Errore nell\'approvazione dei documenti');
      setShowApprovaDocumentiModal(false);
      setErroreValidazione(null);
    }
  };

  // Handler per eventi
  const handleStatoAggiornato = (targetTab = null) => {
    // Ricarica i dati della pratica
    fetchPraticaDetail();
    
    // Se specificata una tab target, naviga ad essa
    if (targetTab) {
      setActiveTab(targetTab);
    }
  };

  const handleDatiAggiornati = () => {
    fetchPraticaDetail();
  };

  const handleDocumentiAggiornati = () => {
    // Eventualmente aggiornare contatori o stati correlati
    fetchPraticaDetail();
  };

  const handleContrattiAggiornati = () => {
    // Eventualmente aggiornare contatori o stati correlati
    fetchPraticaDetail();
  };

  const handleOffertaSelezionata = async (offerta, vettura) => {
    // Funzionalità non ancora implementata nel backend
    // toast.info('Funzionalità di associazione offerta non ancora implementata nel backend');
  };

  const handlePreventiviAggiornati = () => {
    fetchPraticaDetail();
  };

  const handleNavigaAOfferte = () => {
    setActiveTab('offerte');
  };

  const handleAzioniRiepilogo = (azioneId) => {
    switch (azioneId) {
      case 'preventivi':
        setActiveTab('preventivi');
        break;
      case 'modifica_cliente':
        setActiveTab('anagrafica');
        break;
      case 'seleziona_offerta':
        if (!isOfferteTabDisabled()) {
          setActiveTab('offerte');
        } else {
          toast.warning('Le offerte non sono disponibili durante l\'attesa del preventivo');
        }
        break;
      case 'carica_documenti':
      case 'gestisci_documenti':
        setActiveTab('documenti');
        break;
      case 'carica_contratto':
      case 'scarica_contratto':
      case 'carica_firmato':
        setActiveTab('contratti');
        break;
      case 'attiva_contratto':
        attivaContratto();
        break;
      // Nuove azioni per stato "pratica_avviata"
      case 'riassegna_agenzia':
        openRiassegnazione();
        break;
      case 'assegna_agente':
        // Disponibile sia per admin che per agenzia
        openAssegnazioneAgente();
        break;
      case 'supervisione':
        toast.info('Supervisione pratica - tutti i dati sono visibili nelle varie tab');
        break;
      case 'verifica_anagrafica':
        setActiveTab('anagrafica');
        toast.info('🔄 Verifica e aggiorna i dati anagrafici del cliente');
        break;
      case 'richiedi_preventivo':
        setActiveTab('preventivi');
        toast.info('🧾 Richiedi un preventivo personalizzato se necessario');
        break;
      case 'conferma_cliente':
        // Apri direttamente la modal del workflow guidato
        if (gestoreStatiRef.current) {
          gestoreStatiRef.current.apriModalConfermaCliente();
        } else {
          toast.error('Errore nell\'apertura della modal. Usa il pulsante "Workflow Guidato"');
        }
        break;
      case 'richiedi_integrazione':
        richiedeIntegrazione();
        break;
      case 'approva_documenti':
        approvaDocumenti();
        break;
      case 'approva_pratica':
        approvaPratica();
        break;
      case 'condiziona_pratica':
        condizionaPratica();
        break;
      case 'prosegui_da_condizionata':
        approvaPratica();
        break;
      case 'chiudi_condizionata':
        chiudiPraticaCondizionata();
        break;
      case 'rifiuta_pratica':
        rifiutaPratica();
        break;
      case 'torna_pratica_avviata':
        handleTornaPraticaAvviata();
        break;
      default:
        toast.info(`Azione "${azioneId}" in sviluppo - implementazione backend necessaria`);
    }
  };

  // Funzione per gestire la navigazione verso la tab offerte
  const navigaAOfferte = () => {
    setActiveTab('offerte');
  };

  // Funzioni per riassegnazione agenzia
  const fetchAgenzieDisponibili = async () => {
    try {
      const response = await api.get(`${API_CONFIG.BASE_URL}/api/network/nodi`, {
        params: {
          tipoNodo: 'agenzia_primaria',
          attivo: true,
          limit: 100
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success && response.data.data.nodi) {
        setAgenzieDisponibili(response.data.data.nodi);
      } else {
        setAgenzieDisponibili([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento agenzie:', error);
      toast.error('Errore nel caricamento agenzie');
      setAgenzieDisponibili([]);
    }
  };

  const openRiassegnazione = async () => {
    await fetchAgenzieDisponibili();
    setNuovaAgenzia('');
    setShowRiassegnazioneModal(true);
  };

  const riassegnaAgenzia = async () => {
    if (!nuovaAgenzia) {
      toast.error('Seleziona un\'agenzia');
      return;
    }

    setLoadingRiassegnazione(true);
    try {
      const response = await api.put(`${API_CONFIG.BASE_URL}/api/pratica/${id}/riassegna`, {
        nuovaAgenziaId: nuovaAgenzia,
        note: `Pratica riassegnata dall'admin`
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Pratica riassegnata con successo');
      setShowRiassegnazioneModal(false);
      fetchPraticaDetail(); // Ricarica i dati della pratica
    } catch (error) {
      console.error('Errore nella riassegnazione:', error);
      toast.error('Errore nella riassegnazione della pratica');
    } finally {
      setLoadingRiassegnazione(false);
    }
  };

  // Funzioni per assegnazione agente
  const fetchAgentiDisponibili = async () => {
    try {
      // Filtra solo gli agenti dell'agenzia proprietaria della pratica
      const agenziaId = pratica?.idAgenziaPrimaria;
      
      if (!agenziaId) {
        console.error('ID agenzia pratica non trovato');
        setAgentiDisponibili([]);
        return;
      }

      // Ottieni solo gli agenti dell'agenzia della pratica
      const response = await api.get(`${API_CONFIG.BASE_URL}/api/network/utenti/agenti`, {
        params: {
          agenziaId: agenziaId  // Filtra per agenzia specifica
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success && response.data.agenti) {
        // Filtra solo i subagenti, escludendo l'agenzia proprietaria e i team leader
        const subagenti = response.data.agenti.filter(agente => {
          // Escludi gli agenti con ruolo diverso da subagente
          if (agente.role !== 'subagente') return false;
          
          // Escludi i team leader
          if (agente.nodoRete?.tipoNodo === 'team_leader') return false;
          
          return true;
        });
        
        setAgentiDisponibili(subagenti);
      } else {
        setAgentiDisponibili([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento agenti:', error);
      toast.error('Errore nel caricamento agenti');
      setAgentiDisponibili([]);
    }
  };

  const openAssegnazioneAgente = async () => {
    // Controlla se l'utente ha i permessi per assegnare agenti
    if (!canAssegnareAgenti()) {
      toast.warning('Non hai i permessi per assegnare agenti alle pratiche');
      return;
    }
    
    await fetchAgentiDisponibili();
    setNuovoAgente('');
    setShowAssegnazioneAgenteModal(true);
  };

  const assegnaAgente = async () => {
    if (!nuovoAgente) {
      toast.error('Seleziona un agente');
      return;
    }

    setLoadingAssegnazioneAgente(true);
    try {
      const response = await api.put(`${API_CONFIG.BASE_URL}/api/pratica/${id}/assegna-agente`, {
        nuovoAgenteId: nuovoAgente,
        note: `Agente assegnato dall'admin`
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Agente assegnato con successo');
      setShowAssegnazioneAgenteModal(false);
      fetchPraticaDetail(); // Ricarica i dati della pratica
    } catch (error) {
      console.error('Errore nell\'assegnazione agente:', error);
      toast.error('Errore nell\'assegnazione dell\'agente');
    } finally {
      setLoadingAssegnazioneAgente(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento dettagli pratica...</p>
        </div>
      </div>
    );
  }

  if (!pratica) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">Pratica non trovata</h2>
          <p className="mt-2 text-gray-600">La pratica richiesta non esiste o non hai i permessi per visualizzarla.</p>
          <button
            onClick={() => navigate('/nlt/pratiche')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Torna alle pratiche
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Breadcrumb e Info Pratica */}
      <div className="bg-white border-b">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 mb-1">
                ID Pratica: <span className="font-medium text-gray-900">{pratica.codicePratica}</span>
              </div>
              <div className="text-sm text-gray-500">
                Agenzia: <span className="font-medium text-gray-900">{getAgenziaName(pratica.idAgenziaPrimaria)}</span>
              </div>
            </div>
            
            {/* Navigazione Tab */}
            <NavigazioneTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isOfferteTabDisabled={isOfferteTabDisabled}
            />
          </div>
        </div>
      </div>

      {/* Contenuto Principale */}
      <div className="w-full px-6 py-6">
        
        {/* Header Pratica */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {pratica.cliente?.ragione_sociale || 
                 `${pratica.cliente?.nome || ''} ${pratica.cliente?.cognome || ''}`.trim() ||
                 'Cliente'} | ID {pratica.id}
              </h2>
              <div className="flex items-center space-x-6 mt-2 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Numero Pratica:</span> {pratica.codicePratica}
                </div>
                <div>
                  <span className="font-medium">Stato:</span> 
                  <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium`}>
                    {pratica.statoPratica || pratica.stato}
                  </span>
                </div>
                <div>
                  <span className="font-medium">Priorità:</span> 
                  <span className="ml-1 capitalize">{pratica.priorita || 'Normale'}</span>
                </div>
                <div>
                  <span className="font-medium">Data assegnazione:</span> {formatDate(pratica.dataAssegnazione)}
                </div>
                {pratica.temperaturaOriginale && (
                  <div>
                    <span className="font-medium">Temperatura:</span> {pratica.temperaturaOriginale}
                  </div>
                )}
              </div>
            </div>
            
            {/* Selettore Ruolo (per test) */}
            <div className="text-right flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Ruolo:</label>
                <div className="relative">
                  <select
                    value={ruoloUtente}
                    onChange={(e) => setRuoloUtente(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                    title={
                      getRuoloRealeUtente() !== 'admin' 
                        ? `Il tuo ruolo (${getRuoloRealeUtente()}) limita le opzioni disponibili`
                        : 'Simula diversi ruoli per testare le funzionalità'
                    }
                  >
                    {getOpzioniRuoloDisponibili().map((opzione) => (
                      <option key={opzione.value} value={opzione.value}>
                        {opzione.label}
                      </option>
                    ))}
                  </select>
                  {getRuoloRealeUtente() !== 'admin' && (
                    <div className="absolute -top-2 -right-2">
                      <span className="bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center" title="Opzioni limitate dal tuo ruolo">!</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Gestore Stati */}
              <GestoreStatiPratica 
                ref={gestoreStatiRef}
                pratica={pratica}
                ruoloUtente={ruoloUtente}
                onStatoAggiornato={handleStatoAggiornato}
                isPraticaModificabileAgente={isPraticaModificabileAgente}
              />
            </div>
          </div>
        </div>

        {/* Contenuto Tab */}
        <div>
          {activeTab === 'riepilogo' && (
            <TabRiepilogo
              pratica={pratica}
              getAgenziaName={getAgenziaName}
              getAgenteName={getAgenteName}
              formatDate={formatDate}
              ruoloUtente={ruoloUtente}
              onAzioniDisponibili={handleAzioniRiepilogo}
              isOfferteTabDisabled={isOfferteTabDisabled}
              canAssegnareAgenti={canAssegnareAgenti}
              user={user}
              contratti={contratti}
            />
          )}

          {activeTab === 'anagrafica' && (
            <TabAnagrafica
              pratica={pratica}
              onDatiAggiornati={handleDatiAggiornati}
              isCampoModificabileAgente={isCampoModificabileAgente}
            />
          )}

          {activeTab === 'offerte' && (
            <TabOfferte
              pratica={pratica}
              isOfferteTabDisabled={isOfferteTabDisabled}
              ruoloUtente={ruoloUtente}
              onOffertaSelezionata={handleOffertaSelezionata}
              currentUser={user}
            />
          )}

          {activeTab === 'preventivi' && (
            <TabPreventivi 
              pratica={pratica}
              ruoloUtente={ruoloUtente}
              isPraticaModificabileAgente={isPraticaModificabileAgente}
              isInAttesaRevisione={isInAttesaRevisione}
              onNavigaAOfferte={() => setActiveTab('offerte')}
              onPreventiviAggiornati={fetchPraticaDetail}
              currentUser={user}
            />
          )}

          {activeTab === 'documenti' && (
            <TabDocumenti
              pratica={pratica}
              ruoloUtente={ruoloUtente}
              formatDate={formatDate}
              onDocumentiAggiornati={handleDocumentiAggiornati}
            />
          )}

          {activeTab === 'comunicazioni' && (
            <TabComunicazioni
              pratica={pratica}
              currentUser={user}
              getTimeAgo={getTimeAgo}
            />
          )}

          {activeTab === 'contratti' && (
            <TabContratti
              pratica={pratica}
              ruoloUtente={ruoloUtente}
              formatDate={formatDate}
              onContrattiAggiornati={handleContrattiAggiornati}
            />
          )}
        </div>
      </div>

      {/* Modal Richiesta Integrazione */}
      {showRichiestaIntegrazioneModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  🔄 Richiedi Integrazione Documenti
                </h3>
                <button
                  onClick={() => {
                    setShowRichiestaIntegrazioneModal(false);
                    setFormIntegrazioneData({ note: '', motivazione: '', documentiDaIntegrare: [] });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="mt-4 space-y-4">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        La pratica tornerà allo stato "Attesa Documenti Cliente" per permettere al cliente di caricare i documenti mancanti.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="motivazione" className="block text-sm font-medium text-gray-700 mb-2">
                    Motivazione della richiesta *
                  </label>
                  <input
                    type="text"
                    id="motivazione"
                    value={formIntegrazioneData.motivazione}
                    onChange={(e) => setFormIntegrazioneData({
                      ...formIntegrazioneData,
                      motivazione: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="es. Documento patente non leggibile, mancano ultime 3 buste paga..."
                    required
                  />
                </div>

                <div>
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
                    Note aggiuntive *
                  </label>
                  <textarea
                    id="note"
                    rows={4}
                    value={formIntegrazioneData.note}
                    onChange={(e) => setFormIntegrazioneData({
                      ...formIntegrazioneData,
                      note: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Dettagli specifici sui documenti da integrare, istruzioni per il cliente..."
                    required
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end pt-4 border-t mt-6 space-x-2">
                <button
                  onClick={() => {
                    setShowRichiestaIntegrazioneModal(false);
                    setFormIntegrazioneData({ note: '', motivazione: '', documentiDaIntegrare: [] });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleRichiestaIntegrazione}
                  className="px-6 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors flex items-center"
                >
                  <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                  Invia Richiesta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Approva Documenti */}
      {showApprovaDocumentiModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  ✅ Approva Documenti
                </h3>
                <button
                  onClick={() => setShowApprovaDocumentiModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="mt-4 space-y-4">
                {!erroreValidazione ? (
                  <>
                    <div className="bg-green-50 border-l-4 border-green-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-green-700">
                            Approvando i documenti, la pratica avanzerà automaticamente al prossimo stato del workflow.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-md">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Cosa succederà:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Verrà eseguita la validazione automatica dei documenti obbligatori</li>
                        <li>• Se tutti i documenti sono presenti, la pratica avanzerà automaticamente</li>
                        <li>• Se mancano documenti, ti verrà data la possibilità di forzare l'approvazione</li>
                        <li>• Verrà registrata l'approvazione nello storico della pratica</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-red-50 border-l-4 border-red-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">
                            Documenti mancanti rilevati
                          </h3>
                          <p className="text-sm text-red-700 mt-1">
                            {erroreValidazione.messaggio}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-md">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Documenti mancanti:</h4>
                      <ul className="text-sm text-yellow-800 space-y-1">
                        {erroreValidazione.documentiMancanti.map((doc, index) => (
                          <li key={index} className="flex items-center">
                            <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-2" />
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-orange-800">
                            Approvazione Forzata
                          </h3>
                          <p className="text-sm text-orange-700 mt-1">
                            Puoi forzare l'approvazione nonostante i documenti mancanti. Questa azione verrà registrata nello storico.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="text-sm text-gray-500">
                  <strong>Pratica:</strong> {pratica.codicePratica} - {pratica.cliente?.nome} {pratica.cliente?.cognome}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end pt-4 border-t mt-6 space-x-2">
                <button
                  onClick={() => {
                    setShowApprovaDocumentiModal(false);
                    setErroreValidazione(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Annulla
                </button>
                
                {!erroreValidazione ? (
                  <button
                    onClick={() => handleApprovaDocumenti(false)}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                  >
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Approva Documenti
                  </button>
                ) : (
                  <button
                    onClick={() => handleApprovaDocumenti(true)}
                    className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center"
                  >
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                    Forza Approvazione
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Riassegnazione Agenzia */}
      {showRiassegnazioneModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Riassegna Pratica ad Altra Agenzia
                </h3>
                <button
                  onClick={() => setShowRiassegnazioneModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Body */}
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleziona Nuova Agenzia
                  </label>
                  <div className="space-y-2">
                    {agenzieDisponibili.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <BuildingOfficeIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>Nessuna agenzia disponibile</p>
                      </div>
                    ) : (
                      <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg">
                        {agenzieDisponibili.map((agenzia) => {
                          const isSelected = nuovaAgenzia == agenzia.id;
                          return (
                            <div
                              key={agenzia.id}
                              onClick={() => setNuovaAgenzia(agenzia.id)}
                              className={`
                                cursor-pointer p-3 border-b last:border-b-0 transition-all hover:bg-gray-50
                                ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}
                              `}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  <div className={`
                                    h-10 w-10 rounded-full flex items-center justify-center
                                    ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}
                                  `}>
                                    <BuildingOfficeIcon className={`
                                      h-6 w-6 
                                      ${isSelected ? 'text-blue-600' : 'text-gray-600'}
                                    `} />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className={`
                                      text-sm font-medium truncate
                                      ${isSelected ? 'text-blue-900' : 'text-gray-900'}
                                    `}>
                                      {agenzia.nome}
                                    </p>
                                    {isSelected && (
                                      <div className="flex-shrink-0">
                                        <div className="h-4 w-4 bg-blue-500 rounded-full flex items-center justify-center">
                                          <div className="h-2 w-2 bg-white rounded-full"></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    {agenzia.utenteRete ? (
                                      <p className="text-xs text-gray-500">
                                        👤 Ref: {agenzia.utenteRete.nome} {agenzia.utenteRete.cognome}
                                      </p>
                                    ) : (
                                      <p className="text-xs text-gray-400">
                                        👤 Nessun referente
                                      </p>
                                    )}
                                    <span className={`
                                      inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                      ${agenzia.attivo 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                      }
                                    `}>
                                      {agenzia.attivo ? '✅ Attiva' : '❌ Inattiva'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Attenzione
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          La riassegnazione della pratica comporterà il trasferimento di tutti i dati e documenti associati alla nuova agenzia.
                        </p>
                        {nuovaAgenzia && agenzieDisponibili.find(a => a.id == nuovaAgenzia) && (
                          <div className="mt-3 p-3 bg-yellow-100 rounded-lg border border-yellow-300">
                            <h4 className="text-sm font-medium text-yellow-900 mb-2">📋 Anteprima Assegnazione:</h4>
                            <div className="space-y-1">
                              <p className="text-sm text-yellow-800">
                                <strong>🏢 Agenzia:</strong> {agenzieDisponibili.find(a => a.id == nuovaAgenzia)?.nome}
                              </p>
                              {agenzieDisponibili.find(a => a.id == nuovaAgenzia)?.utenteRete && (
                                <p className="text-sm text-yellow-800">
                                  <strong>👤 Referente:</strong> {agenzieDisponibili.find(a => a.id == nuovaAgenzia).utenteRete.nome} {agenzieDisponibili.find(a => a.id == nuovaAgenzia).utenteRete.cognome}
                                </p>
                              )}
                              <p className="text-sm text-yellow-800">
                                <strong>📄 Pratica:</strong> {pratica.codicePratica}
                              </p>
                              <p className="text-sm text-yellow-800">
                                <strong>👥 Cliente:</strong> {pratica.cliente?.ragione_sociale || `${pratica.cliente?.nome} ${pratica.cliente?.cognome}`}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end pt-4 border-t mt-6 space-x-2">
                <button
                  onClick={() => setShowRiassegnazioneModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={riassegnaAgenzia}
                  disabled={loadingRiassegnazione || !nuovaAgenzia}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
                >
                  {loadingRiassegnazione && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  )}
                  <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                  Riassegna Agenzia
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Assegnazione Agente */}
      {showAssegnazioneAgenteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-green-600" />
                  Assegna Agente Specifico
                </h3>
                <button
                  onClick={() => setShowAssegnazioneAgenteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Body */}
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleziona Agente
                  </label>
                  <div className="space-y-2">
                    {agentiDisponibili.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <UserIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>Nessun agente disponibile</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Gli agenti devono appartenere all'agenzia proprietaria della pratica
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg">
                        {agentiDisponibili.map((agente) => {
                          const isSelected = nuovoAgente == agente.id;
                          const getTipoAgenteInfo = (nodo) => {
                            if (!nodo) return { icon: '👤', label: 'Agente', color: 'text-purple-600' };
                            switch (nodo.tipoNodo) {
                              case 'team_leader': 
                                return { 
                                  icon: '👥', 
                                  label: 'Team Leader', 
                                  color: 'text-green-600' 
                                };
                              case 'agente': 
                                return { 
                                  icon: '👤', 
                                  label: 'Agente', 
                                  color: 'text-purple-600' 
                                };
                              default: 
                                return { 
                                  icon: '👤', 
                                  label: 'Subagente', 
                                  color: 'text-blue-600' 
                                };
                            }
                          };
                          const tipoInfo = getTipoAgenteInfo(agente.nodoRete);
                          
                          return (
                            <div
                              key={agente.id}
                              onClick={() => setNuovoAgente(agente.id)}
                              className={`
                                cursor-pointer p-3 border-b last:border-b-0 transition-all hover:bg-gray-50
                                ${isSelected ? 'bg-green-50 border-l-4 border-l-green-500' : ''}
                              `}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  <div className={`
                                    h-10 w-10 rounded-full flex items-center justify-center
                                    ${isSelected ? 'bg-green-100' : 'bg-gray-100'}
                                  `}>
                                    <UserIcon className={`
                                      h-6 w-6 
                                      ${isSelected ? 'text-green-600' : 'text-gray-600'}
                                    `} />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className={`
                                      text-sm font-medium truncate
                                      ${isSelected ? 'text-green-900' : 'text-gray-900'}
                                    `}>
                                      {agente.nome} {agente.cognome}
                                    </p>
                                    {isSelected && (
                                      <div className="flex-shrink-0">
                                        <div className="h-4 w-4 bg-green-500 rounded-full flex items-center justify-center">
                                          <div className="h-2 w-2 bg-white rounded-full"></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <p className="text-xs text-gray-500">
                                      📧 {agente.email}
                                    </p>
                                    <span className={`
                                      inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                      ${agente.nodoRete?.tipoNodo === 'team_leader' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-purple-100 text-purple-800'
                                      }
                                    `}>
                                      {tipoInfo.icon} {tipoInfo.label}
                                    </span>
                                    {agente.nodoRete && (
                                      <span className={`
                                        inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                        ${agente.nodoRete.attivo 
                                          ? 'bg-blue-100 text-blue-800' 
                                          : 'bg-red-100 text-red-800'
                                        }
                                      `}>
                                        {agente.nodoRete.attivo ? '✅ Attivo' : '❌ Inattivo'}
                                      </span>
                                    )}
                                  </div>
                                  {agente.nodoRete?.nome && (
                                    <p className="text-xs text-gray-400 mt-1 truncate">
                                      🏢 {agente.nodoRete.nome}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex">
                    <UserIcon className="h-5 w-5 text-blue-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Informazioni
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          L'assegnazione di un agente specifico consente una gestione più mirata della pratica.
                          L'agente riceverà una notifica dell'assegnazione.
                        </p>
                        {nuovoAgente && agentiDisponibili.find(a => a.id == nuovoAgente) && (
                          <div className="mt-3 p-3 bg-blue-100 rounded-lg border border-blue-300">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">📋 Anteprima Assegnazione:</h4>
                            <div className="space-y-1">
                              <p className="text-sm text-blue-800">
                                <strong>👤 Agente:</strong> {agentiDisponibili.find(a => a.id == nuovoAgente)?.nome} {agentiDisponibili.find(a => a.id == nuovoAgente)?.cognome}
                              </p>
                              <p className="text-sm text-blue-800">
                                <strong>📧 Email:</strong> {agentiDisponibili.find(a => a.id == nuovoAgente)?.email}
                              </p>
                              {agentiDisponibili.find(a => a.id == nuovoAgente)?.nodoRete && (
                                <>
                                  <p className="text-sm text-blue-800">
                                    <strong>🏢 Nodo:</strong> {agentiDisponibili.find(a => a.id == nuovoAgente).nodoRete.nome}
                                  </p>
                                  <p className="text-sm text-blue-800">
                                    <strong>👥 Tipo:</strong> {
                                      agentiDisponibili.find(a => a.id == nuovoAgente).nodoRete.tipoNodo === 'team_leader' 
                                        ? '👥 Team Leader' 
                                        : '👤 Agente'
                                    }
                                  </p>
                                </>
                              )}
                              <p className="text-sm text-blue-800">
                                <strong>📄 Pratica:</strong> {pratica.codicePratica}
                              </p>
                              <p className="text-sm text-blue-800">
                                <strong>👥 Cliente:</strong> {pratica.cliente?.ragione_sociale || `${pratica.cliente?.nome} ${pratica.cliente?.cognome}`}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end pt-4 border-t mt-6 space-x-2">
                <button
                  onClick={() => setShowAssegnazioneAgenteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={assegnaAgente}
                  disabled={loadingAssegnazioneAgente || !nuovoAgente}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center disabled:opacity-50"
                >
                  {loadingAssegnazioneAgente && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  )}
                  <UserIcon className="h-4 w-4 mr-2" />
                  Assegna Agente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PraticaDetailPageModulare; 